import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import styled from 'styled-components';
import { MedalIcon } from 'atoms';
import fontService, { FontWeight, TextType } from 'services/FontService';
import { mobile } from 'services/ui';
import { Colors } from 'context';
import { IGrowthAchievement } from 'core';

interface IGrowthAchievements {
  achievements: IGrowthAchievement[];
  isCareerMember: boolean;
  availableTotal: number;
  lockedTotal: number;
}

const CATEGORY_ORDER = ['learning', 'skills', 'profile', 'career', 'community'];

/**
 * Медали (ТЗ 0039 шаг 4).
 *
 * 🔴 Недоступные вне карьерного центра показываются С ЗАМКОМ, а не прячутся:
 * «доступно участникам» — это и есть апселл, ради которого экран открыт всей
 * школе. Спрятать их значит превратить продающий экран в отчёт.
 *
 * Секретная и не полученная приходит без названия — сюрприз срабатывает один
 * раз, и потратить его заранее нельзя.
 */
export const GrowthAchievements = memo(
  ({ achievements, isCareerMember, availableTotal, lockedTotal }: IGrowthAchievements) => {
    const { t, i18n } = useTranslation();

    const groups = useMemo(() => {
      const byCategory = new Map<string, IGrowthAchievement[]>();
      achievements.forEach((row) => {
        const list = byCategory.get(row.category) ?? [];
        list.push(row);
        byCategory.set(row.category, list);
      });
      return CATEGORY_ORDER.filter((key) => byCategory.has(key)).map((key) => ({
        key,
        rows: byCategory.get(key) ?? [],
      }));
    }, [achievements]);

    return (
      <section>
        <SectionTitle>{t('growth.achievements')}</SectionTitle>
        {!isCareerMember && lockedTotal > 0 && (
          <Notice>
            {t('growth.memberOnlyNotice')}{' '}
            {/* Счётчик отделяет доступное от запертого: «0 из 13» включало
              медали, которых студенту вне центра не получить, и читалось как
              упрёк, а не как предложение. */}
            {t('growth.lockedCount', {
              n: lockedTotal,
              available: availableTotal,
            })}
          </Notice>
        )}

        {groups.map((group) => (
          <Group key={group.key}>
            <GroupTitle>{t(`growth.category.${group.key}`)}</GroupTitle>
            <Grid>
              {group.rows.map((row) => (
                <Medal key={row.code} earned={row.earned} locked={row.locked}>
                  <MedalTop>
                    <NameWithIcon>
                      {/* Значок берётся из КАТЕГОРИИ: поле `icon` у медали
                          пустое во всех строках, и заполнять его вручную никто
                          не станет, а категория есть всегда. Не полученная —
                          серая, но видимая: спрятать её значит убрать то, ради
                          чего экран открыт всей школе. */}
                      <MedalIcon category={row.category} muted={!row.earned} size={30} />
                      <Name>{row.hidden ? t('growth.secretMedal') : row.title}</Name>
                    </NameWithIcon>
                    <Points>+{row.points}</Points>
                  </MedalTop>
                  {!row.hidden && row.description ? (
                    <Description>{row.description}</Description>
                  ) : null}

                  {row.earned ? (
                    <State earned>
                      {/* Дата получения: медаль без даты — просто иконка, а с
                          датой это уже часть истории студента. */}
                      {row.earnedAt
                        ? t('growth.earnedAt', {
                            date: new Date(row.earnedAt).toLocaleDateString(i18n.language),
                          })
                        : t('growth.earned')}
                    </State>
                  ) : row.locked ? (
                    <State>{t('growth.membersOnly')}</State>
                  ) : row.target > 1 ? (
                    <>
                      <Track>
                        <Fill
                          style={{
                            width: `${Math.min(
                              100,
                              Math.round((row.current / row.target) * 100),
                            )}%`,
                          }}
                        />
                      </Track>
                      <State>
                        {t('growth.stepCount', {
                          current: row.current,
                          target: row.target,
                        })}
                      </State>
                    </>
                  ) : (
                    <State>{t('growth.notYet')}</State>
                  )}
                </Medal>
              ))}
            </Grid>
          </Group>
        ))}
      </section>
    );
  },
);

const SectionTitle = styled.h2`
  margin: 0 0 14px;
  ${fontService.text({ type: TextType.medium, weight: FontWeight.bold })}
`;

const Notice = styled.p`
  margin: 0 0 16px;
  padding: 10px 14px;
  border-radius: 8px;
  background: ${Colors.bg_gray};
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small })}
`;

const Group = styled.div`
  margin-bottom: 22px;
`;

const GroupTitle = styled.h3`
  margin: 0 0 10px;
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 12px;

  ${mobile} {
    grid-template-columns: 1fr;
  }
`;

const Medal = styled.div<{ earned: boolean; locked: boolean }>`
  padding: 14px 16px;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  border: 1px solid ${({ earned }) => (earned ? Colors.signal_green : Colors.gray)};
  /* Заблокированную приглушаем, но НЕ прячем и не делаем нечитаемой: её текст
     и есть предложение вступить в карьерный центр. */
  opacity: ${({ earned, locked }) => (earned ? 1 : locked ? 0.75 : 0.95)};
`;

const NameWithIcon = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const MedalTop = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
`;

const Name = styled.span`
  ${fontService.text({ type: TextType.small, weight: FontWeight.bold })}
`;

const Points = styled.span`
  color: ${Colors.gray_dark};
  white-space: nowrap;
  ${fontService.text({ type: TextType.extraSmall, weight: FontWeight.medium })}
`;

const Description = styled.span`
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.extraSmall })}
`;

const State = styled.span<{ earned?: boolean }>`
  /* signal_green (#37AB34) — цвет РАМКИ и значка успеха: как текст на белом
     это 2.98:1 при норме 4.5. Тёмно-зелёный marker_green даёт 7.36 в
     светлой теме и 6.93 в тёмной (правило в context/constants/colors.ts). */
  color: ${({ earned }) => (earned ? Colors.marker_green : Colors.gray_dark)};
  ${fontService.text({ type: TextType.extraSmall, weight: FontWeight.medium })}
`;

const Track = styled.div`
  height: 6px;
  border-radius: 3px;
  background: ${Colors.gray};
  overflow: hidden;
`;

const Fill = styled.div`
  height: 100%;
  background: ${Colors.main};
`;
